import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';

import { Machine, MachineCreate, MachineSlot, MachineStatus, MACHINE_STATUS_LABELS } from '../../../types/machine';
import { useMachineOccupation } from '../../../hooks/queries/useMachines';
import { Atelier } from '../../../types/atelier';
import { ateliersAPI, machineAPI } from '../../../services/api';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import PermissionButton from '../../../components/PermissionButton';
import { extractErrorMessage } from '../../../utils/errorHandler';

const STATUS_OPTIONS: MachineStatus[] = [
  'en_service',
  'hors_service',
  'en_maintenance',
  'en_panne',
];

const STATUS_CHIP_COLOR: Record<MachineStatus, 'success' | 'default' | 'warning' | 'error'> = {
  en_service: 'success',
  hors_service: 'default',
  en_maintenance: 'warning',
  en_panne: 'error',
};

interface FormState {
  code: string;
  name: string;
  atelier_id: number | null;
  status: MachineStatus;
}

const emptyForm: FormState = {
  code: '',
  name: '',
  atelier_id: null,
  status: 'en_service',
};

/** « 14:30 », « demain 08:00 », « jeu. 30/10 08:00 » */
const quand = (iso: string) => {
  const d = new Date(iso);
  const hm = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const jour = (x: Date) => x.toISOString().slice(0, 10);
  const today = new Date();
  const tomorrow = new Date(Date.now() + 86_400_000);
  if (jour(d) === jour(today)) return hm;
  if (jour(d) === jour(tomorrow)) return `demain ${hm}`;
  return `${d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })} ${hm}`;
};
const SOURCE = { planning: 'Gestion Planning', comptoir: 'Comptoir' };

/** Busy now / next slot / free — derived from Gestion Planning and Comptoir étapes, not stored. */
const Occupation: React.FC<{ slots?: { actuelle: MachineSlot | null; prochaine: MachineSlot | null; non_termines: number } }> = ({ slots }) => {
  const cur = slots?.actuelle;
  const next = slots?.prochaine;
  // long projet names would widen the table: the reference is what matters
  const what = (s: MachineSlot) => {
    const lib = s.libelle && s.libelle.length > 40 ? `${s.libelle.slice(0, 40)}…` : s.libelle;
    return [s.reference, lib].filter(Boolean).join(' · ');
  };
  return (
    <Box>
      {cur ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="Occupée" size="small" color="warning" />
          <Typography variant="body2">
            {what(cur)}{' '}
            {cur.depasse
              ? <Box component="span" sx={{ color: 'warning.dark' }}>· depuis le {new Date(cur.debut).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} {new Date(cur.debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} (non terminé)</Box>
              : <>jusqu'à {quand(cur.fin)}</>}
          </Typography>
        </Box>
      ) : (
        <Chip label="Libre" size="small" variant="outlined" />
      )}
      {next && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Prochaine : {what(next)} · {quand(next.debut)} ({SOURCE[next.source]})
        </Typography>
      )}
      {cur && (
        <Typography variant="caption" color="text.secondary" display="block">
          {SOURCE[cur.source]}
          {cur.depasse && (slots?.non_termines || 0) > 1 && ` · + ${(slots?.non_termines || 0) - 1} autre(s) non terminé(s)`}
        </Typography>
      )}
    </Box>
  );
};

const MachineManagement: React.FC = () => {
  const { data: occupation } = useMachineOccupation();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [deletedMachines, setDeletedMachines] = useState<Machine[]>([]);
  const [ateliers, setAteliers] = useState<Atelier[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingMachine, setEditingMachine] = useState<Machine | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  useEffect(() => {
    fetchMachines();
    fetchAteliers();
  }, []);

  const fetchMachines = async () => {
    try {
      setLoading(true);
      const data = await machineAPI.getAll();
      setMachines(data.items);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedMachines = async () => {
    try {
      setLoading(true);
      const data = await machineAPI.getDeleted();
      setDeletedMachines(data.items);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchAteliers = async () => {
    try {
      const data = await ateliersAPI.getAll();
      setAteliers(data.items);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const ateliersById = useMemo(() => {
    const map: Record<number, Atelier> = {};
    ateliers.forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, [ateliers]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchDeletedMachines();
    }
  };

  const handleOpen = async (machine?: Machine) => {
    if (machine) {
      setEditingMachine(machine);
      setForm({
        code: machine.code,
        name: machine.name,
        atelier_id: machine.atelier_id,
        status: machine.status,
      });
      setEditMode(true);
    } else {
      setEditingMachine(null);
      try {
        const next = await machineAPI.getNextCode();
        setForm({ ...emptyForm, code: next.code });
      } catch {
        setForm(emptyForm);
      }
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingMachine(null);
    setForm(emptyForm);
    setEditMode(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Le nom est obligatoire');
      return;
    }
    if (!form.atelier_id) {
      showError("L'atelier est obligatoire");
      return;
    }

    try {
      setLoading(true);
      if (editMode && editingMachine) {
        await machineAPI.update(editingMachine.id, {
          name: form.name.trim(),
          atelier_id: form.atelier_id,
          status: form.status,
        });
        showSuccess('Machine modifiée avec succès');
      } else {
        const payload: MachineCreate = {
          name: form.name.trim(),
          atelier_id: form.atelier_id,
          status: form.status,
        };
        await machineAPI.create(payload);
        showSuccess('Machine créée avec succès');
      }
      await fetchMachines();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (machine: Machine) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer la machine "${machine.code} — ${machine.name}" ?`,
      async () => {
        try {
          setLoading(true);
          await machineAPI.delete(machine.id);
          showSuccess('Machine supprimée avec succès');
          await fetchMachines();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleRestore = async (machine: Machine) => {
    try {
      setLoading(true);
      await machineAPI.restore(machine.id);
      showSuccess('Machine restaurée avec succès');
      await Promise.all([fetchDeletedMachines(), fetchMachines()]);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Gestion des Machines</Typography>
        {tabValue === 0 && (
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Machines"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Nouvelle Machine
          </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Machines Actives" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={getModuleTableTheme('production')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={modernCellStyles}>Code</TableCell>
                  <TableCell sx={modernCellStyles}>Nom</TableCell>
                  <TableCell sx={modernCellStyles}>Atelier</TableCell>
                  <TableCell sx={modernCellStyles}>Statut</TableCell>
                  <TableCell sx={modernCellStyles}>Occupation</TableCell>
                  <TableCell sx={modernCellStyles} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {machines.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.code}</TableCell>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.atelier_name || ateliersById[m.atelier_id]?.name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={MACHINE_STATUS_LABELS[m.status]}
                        color={STATUS_CHIP_COLOR[m.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell><Occupation slots={occupation?.[String(m.id)]} /></TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="Config"
                        operation="edit"
                        miniModuleName="Machines"
                        component="IconButton"
                        onClick={() => handleOpen(m)}
                        color="primary"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Config"
                        operation="delete"
                        miniModuleName="Machines"
                        component="IconButton"
                        onClick={() => handleDelete(m)}
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))}
                {machines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucune machine trouvée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box sx={getModuleTableTheme('production')}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={modernCellStyles}>Code</TableCell>
                  <TableCell sx={modernCellStyles}>Nom</TableCell>
                  <TableCell sx={modernCellStyles}>Atelier</TableCell>
                  <TableCell sx={modernCellStyles}>Date de Suppression</TableCell>
                  <TableCell sx={modernCellStyles} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedMachines.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.code}</TableCell>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.atelier_name || ateliersById[m.atelier_id]?.name || '-'}</TableCell>
                    <TableCell>
                      {m.deleted_at ? new Date(m.deleted_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir restaurer la machine "${m.code} — ${m.name}" ?`,
                            () => handleRestore(m),
                            {
                              title: 'Confirmer la restauration',
                              confirmText: 'Restaurer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {deletedMachines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Aucune machine supprimée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Modifier la Machine' : 'Nouvelle Machine'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                variant="filled"
                fullWidth
                label="Code"
                value={form.code}
                InputProps={{ readOnly: true }}
                helperText={editMode ? undefined : 'Auto-généré'}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="filled"
                fullWidth
                label="Nom"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                options={ateliers}
                getOptionLabel={(o) => `${o.code} — ${o.name}`}
                value={ateliers.find((a) => a.id === form.atelier_id) || null}
                onChange={(_, value) => setForm({ ...form, atelier_id: value ? value.id : null })}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} variant="filled" label="Atelier" required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="filled" fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={form.status}
                  label="Statut"
                  onChange={(e) => setForm({ ...form, status: e.target.value as MachineStatus })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {MACHINE_STATUS_LABELS[s]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default MachineManagement;
